import { all, call, put, select } from 'redux-saga/effects';
import {
  JOB_ACTIONS,
  JOB_CANCEL,
  JOB_STARTED,
  JOB_STATUSES,
  JOB_STOP,
  JOB_UPDATED,
  READ_ALL_JOBS,
  READ_JOB,
  STOP_JOB_DISCOVERY_DIALOGUE_NAME,
} from './constants';

import { toggleConfirmDialog } from 'containers/ConfirmDialog/actions';
import { takeEvery } from 'containers/ErrorBoundary';
import get from 'lodash/get';
import { displayMessage } from 'utils/request/displayMessage';
import { requestForSaga } from 'utils/request/request';
import { requestCustomErrorMessage } from 'utils/request/requestCustomErrorMessage';
import { setStopJobId } from './actions';

export function* getJobs() {
  return yield select((s) => s.getIn(['jobManagement', 'jobs']));
}

export function* isDiscoveryRunByUser() {
  const jobs = yield getJobs();
  const foundJob = !jobs
    ? false
    : jobs.find(
        (job) =>
          job.get('action') === 'discovery' &&
          job.get('cron') === false &&
          job.get('status') !== 'error',
      );
  if (!foundJob) {
    return false;
  }
  return true;
}

export function* jobUpdatedSaga(action) {
  try {
    const payload = get(action, 'payload', []);
    if (Array.isArray(payload)) {
      const jobDownloadReady = payload.find(
        (job) =>
          job.downloadFile &&
          job.done &&
          !job.read &&
          job.action === JOB_ACTIONS.snapshotDownload,
      );
      if (jobDownloadReady) {
        yield call(
          displayMessage,
          'success',
          'jobManagement.request.snapshotDownloadReady',
          undefined,
          undefined,
          true,
        );
      }
      const configurationExportDownloadReady = payload.find(
        (job) =>
          job.downloadFile &&
          job.done &&
          !job.read &&
          job.action === JOB_ACTIONS.configurationExport,
      );
      if (configurationExportDownloadReady) {
        yield call(
          displayMessage,
          'success',
          'jobManagement.request.configurationExportReady',
        );
      }
      const configurationImported = payload.find(
        (job) =>
          job.done &&
          !job.read &&
          job.status !== JOB_STATUSES.error &&
          job.action === JOB_ACTIONS.configurationImport,
      );
      if (configurationImported) {
        yield call(
          displayMessage,
          'success',
          'jobManagement.request.configurationImported',
        );
      }
      // The dropdown entry alone gives no visible completion signal (the bell
      // only animates when the job list grows), so the finished path-lookup is
      // announced by a toast; the entry keeps the "View devices" link.
      const aimFlowPathLookupDone = payload.find(
        (job) =>
          job.done &&
          !job.read &&
          job.status !== JOB_STATUSES.error &&
          job.action === JOB_ACTIONS.aimFlowPathLookup,
      );
      if (aimFlowPathLookupDone) {
        yield call(
          displayMessage,
          'success',
          'jobManagement.request.aimFlowPathLookupDone',
        );
      }
      const techsupportReady = payload.find(
        (job) =>
          job.downloadFile &&
          job.done &&
          !job.read &&
          job.action === JOB_ACTIONS.techsupport,
      );
      if (techsupportReady) {
        yield call(
          displayMessage,
          'success',
          'jobManagement.request.techsupportReady',
        );
      }
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* jobStartedSaga(action) {
  try {
    const payload = get(action, 'payload', []);
    if (Array.isArray(payload)) {
      const loadGraphCacheStarted = payload.find(
        (job) =>
          job.running && !job.read && job.action === JOB_ACTIONS.loadGraphCache,
      );
      if (loadGraphCacheStarted) {
        yield call(
          displayMessage,
          'success',
          'jobManagement.request.loadGraphCache',
        );
      }
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* jobCancelSaga(action) {
  try {
    yield call(requestForSaga, {
      url: `/jobs/${action.payload}/cancel`,
      method: 'POST',
    });
    yield put({
      type: `${JOB_CANCEL}_SUCCESS`,
      payload: action.payload,
    });
  } catch (e) {
    yield put({
      type: `${JOB_CANCEL}_ERROR`,
      payload: action.payload,
    });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* jobStopSaga(action) {
  try {
    yield call(requestForSaga, {
      url: `/jobs/${action.payload}/stop`,
      method: 'POST',
    });
    yield put({
      type: `${JOB_STOP}_SUCCESS`,
      payload: action.payload,
    });
  } catch (e) {
    yield put({
      type: `${JOB_STOP}_ERROR`,
      payload: action.payload,
    });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* readJobSaga(action) {
  const { id, force } = action.payload;
  try {
    yield call(requestForSaga, {
      url: `/jobs/${id}/read`,
      method: 'POST',
      data: force ? { force: true } : {},
    });
    yield put({
      type: `${READ_JOB}_SUCCESS`,
      payload: action.payload,
    });
  } catch (e) {
    yield put({
      type: `${READ_JOB}_ERROR`,
      payload: action.payload,
    });
    if (get(e, 'code') === 'API_FILE_NOT_DOWNLOADED') {
      yield put(setStopJobId({ id, action: 'read' }));
      yield put(toggleConfirmDialog(STOP_JOB_DISCOVERY_DIALOGUE_NAME));
    } else {
      yield call(requestCustomErrorMessage, 'danger', e);
      throw e;
    }
  }
}

export function* readAllJobsSaga(action) {
  const { force } = action.payload;
  try {
    yield call(requestForSaga, {
      url: '/jobs/read',
      method: 'POST',
      data: force ? { force: true } : false,
    });
    yield put({ type: `${READ_ALL_JOBS}_SUCCESS` });
  } catch (e) {
    yield put({ type: `${READ_ALL_JOBS}_ERROR` });
    if (get(e, 'code') === 'API_FILE_NOT_DOWNLOADED') {
      yield put(setStopJobId({ id: 'all', action: 'read' }));
      yield put(toggleConfirmDialog(STOP_JOB_DISCOVERY_DIALOGUE_NAME));
    } else {
      yield call(requestCustomErrorMessage, 'danger', e);
      throw e;
    }
  }
}

export function* defaultSaga() {
  yield all([
    takeEvery(JOB_CANCEL, jobCancelSaga),
    takeEvery(JOB_STOP, jobStopSaga),
    takeEvery(JOB_STARTED, jobStartedSaga),
    takeEvery(READ_JOB, readJobSaga),
    takeEvery(READ_ALL_JOBS, readAllJobsSaga),
    takeEvery(JOB_UPDATED, jobUpdatedSaga),
  ]);
}

export default defaultSaga;
